import { apiRequest, apiResponse } from "./client";

export type ImportJobStatus = "VALIDATED" | "CONFIRMED";
export type ImportRowStatus = "VALID" | "INVALID" | "IMPORTED" | "FAILED_RECHECK";

export interface ImportRowError {
  field: string;
  raw_value: unknown;
  normalized_value: unknown;
  code: string;
  reason: string;
}

export interface ProjectImportRow {
  id: string;
  row_number: number;
  status: ImportRowStatus;
  raw_values: Record<string, unknown>;
  normalized_values: Record<string, unknown> | null;
  errors: ImportRowError[];
  project_id: string | null;
  project_number: string | null;
}

export interface ProjectImportJob {
  id: string;
  original_filename: string;
  file_sha256: string;
  template_version: string;
  status: ImportJobStatus;
  total_rows: number;
  valid_rows: number;
  failed_rows: number;
  imported_rows: number;
  created_at: string;
  updated_at: string;
  rows: ProjectImportRow[];
}

export interface BasicProjectImportError extends ImportRowError {
  row_number: number;
}

export interface BasicProjectImportRow {
  row_number: number;
  work_name: string;
  work_type_code: string;
  rights_mode_code: string;
  completion_date: string;
  publication_date: string | null;
  work_summary: string;
  creation_process: string;
  cloud_drive_info: string | null;
}

export interface BasicProjectImportValidationResult {
  total_rows: number;
  valid_rows: number;
  failed_rows: number;
  rows: BasicProjectImportRow[];
  errors: BasicProjectImportError[];
}

export function validateProjectImport(file: File): Promise<ProjectImportJob> {
  return apiRequest("/api/v1/project-imports/validate", {
    method: "POST",
    headers: {
      "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "X-Filename": encodeURIComponent(file.name),
    },
    body: file,
  });
}

export function validateBasicProjectImport(
  file: File,
): Promise<BasicProjectImportValidationResult> {
  return apiRequest("/api/v1/project-imports/basic-validate", {
    method: "POST",
    headers: {
      "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "X-Filename": encodeURIComponent(file.name),
    },
    body: file,
  });
}

export function confirmProjectImport(
  jobId: string,
  idempotencyKey: string,
): Promise<ProjectImportJob> {
  return apiRequest(`/api/v1/project-imports/${jobId}/confirm`, {
    method: "POST",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

export async function downloadProjectImportTemplate(): Promise<Blob> {
  const response = await apiResponse("/api/v1/project-imports/template", {
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });
  return response.blob();
}

export async function downloadBasicProjectImportTemplate(): Promise<Blob> {
  const response = await apiResponse("/api/v1/project-imports/basic-template", {
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  });
  return response.blob();
}

export async function downloadProjectImportErrors(jobId: string): Promise<Blob> {
  const response = await apiResponse(`/api/v1/project-imports/${jobId}/errors.csv`, {
    headers: { Accept: "text/csv" },
  });
  return response.blob();
}
